import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { buildKey, buildLabel } from '../utils/labelBuilder';
import { drinkFamilies, modifierOptions } from '../data/drinkData';

const { FiPlus, FiMinus, FiSettings, FiX } = FiIcons;

const Composer = ({ onAdd, isVisible, onToggle }) => {
  const [family, setFamily] = useState('kopi');
  const [milkOrBlack, setMilkOrBlack] = useState('default');
  const [sweetness, setSweetness] = useState('default');
  const [strength, setStrength] = useState('default');
  const [special, setSpecial] = useState('none');
  const [temperature, setTemperature] = useState('hot');
  const [quantity, setQuantity] = useState(1);

  const isKopiTeh = family === 'kopi' || family === 'teh';

  // Reset modifiers when family changes
  useEffect(() => {
    if (!isKopiTeh) {
      setMilkOrBlack('default');
      setSweetness('default');
      setStrength('default');
      setSpecial('none');
    } else {
      // Validate special option for current family
      if (special === 'gu-you' && family !== 'kopi') {
        setSpecial('none');
      }
      if (special === 'halia' && family !== 'teh') {
        setSpecial('none');
      }
    }
  }, [family, isKopiTeh, special]);

  const getValidSpecialOptions = () => {
    const baseOptions = [
      { value: 'none', label: 'None' },
      { value: 'tarik', label: 'Tarik' }
    ];

    if (family === 'kopi') {
      return [...baseOptions, { value: 'gu-you', label: 'Gu You' }];
    }
    if (family === 'teh') {
      return [...baseOptions, { value: 'halia', label: 'Halia' }];
    }
    return baseOptions;
  };

  const handleQuantityDecrease = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setQuantity(Math.max(1, quantity - 1));
  };

  const handleQuantityIncrease = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setQuantity(quantity + 1);
  };

  const handleAddToTally = () => {
    const key = buildKey({
      family,
      milkOrBlack: isKopiTeh ? milkOrBlack : undefined,
      sweetness: isKopiTeh ? sweetness : undefined,
      strength: isKopiTeh ? strength : undefined,
      special: isKopiTeh ? special : undefined,
      temperature
    });

    onAdd(key, quantity);
    setQuantity(1);
    onToggle(); // Close composer after adding
  };

  const previewKey = buildKey({
    family,
    milkOrBlack: isKopiTeh ? milkOrBlack : undefined,
    sweetness: isKopiTeh ? sweetness : undefined,
    strength: isKopiTeh ? strength : undefined,
    special: isKopiTeh ? special : undefined,
    temperature
  });
  const previewLabel = buildLabel(previewKey);

  return (
    <>
      {/* Toggle Button */}
      <motion.button
        whileTap={{ scale: 0.95 }}
        whileHover={{ scale: 1.02 }}
        onClick={onToggle}
        className={`w-full ${isVisible ? 'bg-gradient-to-r from-red-500 to-red-600' : 'bg-gradient-to-r from-amber-500 via-orange-500 to-red-500'} hover:shadow-xl text-white font-bold py-4 px-6 rounded-xl transition-all duration-300 shadow-lg min-h-[52px] flex items-center justify-center space-x-2`}
      >
        <SafeIcon icon={isVisible ? FiX : FiSettings} />
        <span>{isVisible ? 'Close Custom Order' : 'Create Custom Order'}</span>
      </motion.button>

      {/* Composer Panel */}
      <AnimatePresence>
        {isVisible && (
          <motion.section 
            initial={{ opacity: 0, height: 0, y: -20 }}
            animate={{ opacity: 1, height: 'auto', y: 0 }}
            exit={{ opacity: 0, height: 0, y: -20 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 p-6 space-y-6 overflow-hidden"
          >
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-gradient-to-br from-amber-400 to-orange-500 rounded-lg flex items-center justify-center">
                <SafeIcon icon={FiSettings} className="text-white text-sm" />
              </div>
              <h2 className="text-2xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
                Custom Order
              </h2>
            </div>

            {/* Family Selection */}
            <div className="space-y-3">
              <label className="block text-sm font-semibold text-gray-700">Drink Family</label>
              <select
                value={family}
                onChange={(e) => setFamily(e.target.value)}
                className="w-full p-4 bg-gray-50/50 backdrop-blur-sm border border-gray-200/50 rounded-xl focus:ring-2 focus:ring-amber-400/50 focus:border-amber-400 transition-all duration-200 min-h-[48px] text-gray-800 font-medium"
              >
                {drinkFamilies.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>

            {/* Kopi/Teh Modifiers */}
            {isKopiTeh && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <label className="block text-sm font-semibold text-gray-700">Milk/Black</label>
                    <select
                      value={milkOrBlack}
                      onChange={(e) => setMilkOrBlack(e.target.value)}
                      className="w-full p-4 bg-gray-50/50 backdrop-blur-sm border border-gray-200/50 rounded-xl focus:ring-2 focus:ring-amber-400/50 focus:border-amber-400 transition-all duration-200 min-h-[48px] text-gray-800 font-medium"
                    >
                      {modifierOptions.milkOrBlack.map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-3">
                    <label className="block text-sm font-semibold text-gray-700">Sweetness</label>
                    <select
                      value={sweetness}
                      onChange={(e) => setSweetness(e.target.value)}
                      className="w-full p-4 bg-gray-50/50 backdrop-blur-sm border border-gray-200/50 rounded-xl focus:ring-2 focus:ring-amber-400/50 focus:border-amber-400 transition-all duration-200 min-h-[48px] text-gray-800 font-medium"
                    >
                      {modifierOptions.sweetness.map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <label className="block text-sm font-semibold text-gray-700">Strength</label>
                    <select
                      value={strength}
                      onChange={(e) => setStrength(e.target.value)}
                      className="w-full p-4 bg-gray-50/50 backdrop-blur-sm border border-gray-200/50 rounded-xl focus:ring-2 focus:ring-amber-400/50 focus:border-amber-400 transition-all duration-200 min-h-[48px] text-gray-800 font-medium"
                    >
                      {modifierOptions.strength.map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-3">
                    <label className="block text-sm font-semibold text-gray-700">Special</label>
                    <select
                      value={special}
                      onChange={(e) => setSpecial(e.target.value)}
                      className="w-full p-4 bg-gray-50/50 backdrop-blur-sm border border-gray-200/50 rounded-xl focus:ring-2 focus:ring-amber-400/50 focus:border-amber-400 transition-all duration-200 min-h-[48px] text-gray-800 font-medium"
                    >
                      {getValidSpecialOptions().map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </>
            )}

            {/* Temperature */}
            <div className="space-y-3">
              <label className="block text-sm font-semibold text-gray-700">Temperature</label>
              <select
                value={temperature}
                onChange={(e) => setTemperature(e.target.value)}
                className="w-full p-4 bg-gray-50/50 backdrop-blur-sm border border-gray-200/50 rounded-xl focus:ring-2 focus:ring-amber-400/50 focus:border-amber-400 transition-all duration-200 min-h-[48px] text-gray-800 font-medium"
              >
                {modifierOptions.temperature.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>

            {/* Quantity */}
            <div className="space-y-3">
              <label className="block text-sm font-semibold text-gray-700">Quantity</label>
              <div className="flex items-center justify-center space-x-4">
                <motion.button
                  whileTap={{ scale: 0.95 }}
                  onClick={handleQuantityDecrease}
                  disabled={quantity <= 1}
                  className="p-3 rounded-xl bg-gradient-to-br from-red-50 to-red-100 hover:from-red-100 hover:to-red-200 disabled:from-gray-50 disabled:to-gray-100 disabled:cursor-not-allowed text-red-600 disabled:text-gray-400 transition-all duration-200 min-w-[48px] min-h-[48px] flex items-center justify-center shadow-lg hover:shadow-xl disabled:shadow-sm"
                  aria-label="Decrease quantity"
                >
                  <SafeIcon icon={FiMinus} className="text-lg" />
                </motion.button>

                <div className="px-6 py-3 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border border-gray-200/50">
                  <span className="text-3xl font-bold text-gray-800 min-w-[3rem] text-center block">
                    {quantity}
                  </span>
                </div>

                <motion.button
                  whileTap={{ scale: 0.95 }}
                  onClick={handleQuantityIncrease}
                  className="p-3 rounded-xl bg-gradient-to-br from-green-50 to-green-100 hover:from-green-100 hover:to-green-200 text-green-600 transition-all duration-200 min-w-[48px] min-h-[48px] flex items-center justify-center shadow-lg hover:shadow-xl"
                  aria-label="Increase quantity"
                >
                  <SafeIcon icon={FiPlus} className="text-lg" />
                </motion.button>
              </div>
            </div>

            {/* Preview */}
            <motion.div 
              layout
              className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200/50 rounded-xl p-4 backdrop-blur-sm"
            >
              <p className="text-sm font-medium text-amber-700 mb-2">Preview:</p>
              <p className="text-xl font-bold bg-gradient-to-r from-amber-700 to-orange-700 bg-clip-text text-transparent">
                {previewLabel}
              </p>
            </motion.div>

            {/* Add Button */}
            <motion.button
              whileTap={{ scale: 0.98 }}
              whileHover={{ scale: 1.02 }}
              onClick={handleAddToTally}
              className="w-full bg-gradient-to-r from-green-500 via-emerald-500 to-teal-500 hover:from-green-600 hover:via-emerald-600 hover:to-teal-600 text-white font-bold py-4 px-6 rounded-xl transition-all duration-300 shadow-2xl hover:shadow-3xl min-h-[52px] relative overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
              <span className="relative flex items-center justify-center space-x-2">
                <span>Add to Tally ({quantity})</span>
                <SafeIcon icon={FiPlus} />
              </span>
            </motion.button>
          </motion.section>
        )}
      </AnimatePresence>
    </>
  );
};

export default Composer;